import { useState } from "react";
import { card, theme } from "../theme";

// Public (unauthenticated) page where a competent authority reads one shared submission —
// /submission-share#<token>, with the pass key typed on arrival.
//
// The token is in the URL *fragment*, not the query string: a fragment is never sent to a server,
// so it stays out of request logs, proxy logs and referrer headers even though the link travels by
// email. It is half a credential; the pass key, sent in its own email, is the other half. Both go
// to the API in the request body.
//
// Nothing else is reachable from here: no session, no organisation profile, no other submission,
// no evidence. One submission, as it was sent.

const API = import.meta.env.VITE_API_BASE_URL as string;

interface FieldDef { key: string; label: string; required: boolean; basis: string; kind: string }
interface Submission {
  displayId: number | null; kind: string; regime: string | null; authority: string; status: string;
  payload: Record<string, unknown>; awareAt: string | null; dueAt: string | null;
  submittedAt: string | null; reference: string | null;
}
interface SharedView {
  organisation: string; sharedWith: string; expiresAt: string;
  submission: Submission; fields: FieldDef[];
}

const words = (s: string) => s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

const fmt = (value: unknown, kind: string): string => {
  if (value === null || value === undefined || value === "") return "— not answered —";
  if (kind === "boolean") return value === true || value === "true" ? "Yes" : "No";
  if (kind === "date") {
    const d = new Date(String(value));
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString("en-GB", { dateStyle: "long", timeStyle: "short" });
  }
  return String(value);
};

export function SubmissionSharePage() {
  const [token] = useState(() => window.location.hash.replace(/^#/, ""));
  const [passKey, setPassKey] = useState("");
  const [view, setView] = useState<SharedView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function open(e: React.FormEvent) {
    e.preventDefault();
    if (!token || !passKey.trim() || busy) return;
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API}/submission-share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, passKey: passKey.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        // A server fault is never shown raw ("Internal server error") to the authority reading this.
        const fallback = res.status >= 500 ? "The submission cannot be opened right now. Please try again later." : "This link is not valid.";
        const message = typeof body?.error === "string" && !(res.status >= 500 && /internal server error/i.test(body.error)) ? body.error : fallback;
        throw new Error(message);
      }
      setView(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : "This link is not valid.");
    } finally { setBusy(false); }
  }

  const page: React.CSSProperties = {
    minHeight: "100vh", background: theme.color.bg, color: theme.color.text,
    fontFamily: theme.font, padding: "40px 16px",
  };
  const panel: React.CSSProperties = { ...card, maxWidth: 820, margin: "0 auto", padding: 24 };

  if (!token) {
    return (
      <div style={page}>
        <div style={panel}>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: theme.color.heading }}>Nothing to open</h1>
          <p style={{ fontSize: 13, color: theme.color.subtle, marginTop: 8 }}>
            This page opens a link an organisation sent you. Use the full link from the email —
            copying only part of it leaves the reference behind.
          </p>
        </div>
      </div>
    );
  }

  if (!view) {
    return (
      <div style={page}>
        <form style={panel} onSubmit={open}>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: theme.color.heading }}>Regulatory submission</h1>
          <p style={{ fontSize: 13, color: theme.color.subtle, marginTop: 8, lineHeight: 1.5 }}>
            An organisation has shared one submission with you. Enter the pass key from the second
            email it sent. The link and the pass key travel separately on purpose, so neither on its
            own opens anything.
          </p>
          <input
            value={passKey}
            onChange={(e) => setPassKey(e.target.value)}
            placeholder="ABCD-EFGH-JKMN-PQRS"
            autoFocus
            autoComplete="off"
            spellCheck={false}
            style={{
              width: "100%", marginTop: 16, padding: "10px 12px", fontSize: 16, letterSpacing: "1px",
              fontFamily: "monospace", textTransform: "uppercase", background: "var(--c-panelAlt)",
              border: `1px solid ${theme.color.border}`, borderRadius: 3, color: theme.color.heading,
            }}
          />
          {error && <div style={{ marginTop: 12, fontSize: 12, color: theme.color.danger }}>{error}</div>}
          <button
            type="submit"
            disabled={busy || !passKey.trim()}
            style={{
              marginTop: 16, padding: "9px 18px", fontSize: 13, fontWeight: 600, borderRadius: 3,
              border: "none", cursor: "pointer", background: theme.color.accent, color: "#fff",
              opacity: busy || !passKey.trim() ? 0.6 : 1,
            }}
          >
            {busy ? "Opening…" : "Open submission"}
          </button>
        </form>
      </div>
    );
  }

  const { submission, fields } = view;

  return (
    <div style={page}>
      <div style={panel}>
        <div style={{ fontSize: 11, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.5px" }}>
          {words(submission.kind)}
        </div>
        <h1 style={{ margin: "4px 0 0", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
          {view.organisation}
          {submission.displayId !== null && <span style={{ fontSize: 13, fontWeight: 500, color: theme.color.faint }}> · SUB-{submission.displayId}</span>}
        </h1>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 6, lineHeight: 1.6 }}>
          To {submission.authority}{submission.regime ? ` · ${submission.regime}` : ""}<br />
          {submission.submittedAt
            ? `Sent ${new Date(submission.submittedAt).toLocaleString("en-GB", { dateStyle: "long", timeStyle: "short" })}${submission.reference ? ` · reference ${submission.reference}` : ""}`
            : `Status: ${words(submission.status)} — not yet recorded as sent`}<br />
          Shared with {view.sharedWith} · link expires {new Date(view.expiresAt).toLocaleDateString("en-GB", { dateStyle: "long" })}
        </div>

        <div style={{ marginTop: 20, borderTop: `1px solid ${theme.color.border}` }}>
          {fields.map((f) => (
            <div key={f.key} style={{ padding: "12px 0", borderBottom: `1px solid ${theme.color.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.text }}>{f.label}</div>
              <div style={{ fontSize: 13, color: theme.color.heading, marginTop: 4, whiteSpace: "pre-wrap" }}>
                {fmt(submission.payload?.[f.key], f.kind)}
              </div>
              {f.basis && <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>{f.basis}</div>}
            </div>
          ))}
        </div>

        <p style={{ fontSize: 11, color: theme.color.faint, marginTop: 16, lineHeight: 1.5 }}>
          This is the submission as the organisation recorded it, and nothing else. Your access has
          been logged for the organisation's audit trail. Print this page if you need a copy — the
          link expires and can be withdrawn.
        </p>
      </div>
    </div>
  );
}
